import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { commonEnumValidator } from '@zaoblako/shared/modules/serde/validators/common-enum.validator';
import { CatalogDtosKeysEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-dtos-keys.enum';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { booleanSchema } from '@zaoblako/shared/modules/serde/schemas/boolean.schema';
import { AgentTypeEnum } from '@zaoblako/shared/modules/ai/enums/agent-type.enum';
import { AgentTypeEnumI18n } from '@zaoblako/shared/modules/ai/i18n/agent-type-enum.i18n';
import { AgentOperatingModeEnum } from '@zaoblako/shared/modules/ai/enums/agent-operating-mode.enum';
import { AgentOperatingModeEnumI18n } from '@zaoblako/shared/modules/ai/i18n/agent-operating-mode-enum.i18n';
import { KnowledgeBaseCategoryEnum } from '@zaoblako/shared/modules/ai/enums/knowledge-base-category.enum';
import { KnowledgeBaseCategoryEnumI18n } from '@zaoblako/shared/modules/ai/i18n/knowledge-base-category-enum.i18n';
import type { SerdeContextDataInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context-data.interface';
import type { SerdeContextInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context.interface';

import * as yup from 'yup';

/** Agent create/update request body */
export interface AgentFormInterface {
  /** Agent display name */
  name: string;
  /** Agent model name */
  model?: string;
  /** Agent type */
  type: AgentTypeEnum;
  /** Agent operating mode */
  operatingMode: AgentOperatingModeEnum;
  /** Agent prompt */
  prompt: string;
  /** Accuracy of answers (range by 0.0 - 1.0) */
  temperature: number;
  /** Minimum reliability for responses (range by 0.0 - 1.0) */
  minConfidence: number;
  /** Active agent */
  isActive: boolean;
  /** Agent has embedding function */
  isEmbedding: boolean;
  /** Default agent for embeddings / knowledge-base search for agents without embedding */
  isEmbeddingDefault?: boolean;
  /** Allows the agent to automatically answer questions */
  autoReply: boolean;
  /** Agent api key */
  apiKey: string;
  /** If `true` then this agent will be used by voice assistant */
  isVoiceAssistantDefault?: boolean;
  /** Agent folder id */
  folderID?: string;
  /** Agent base url */
  baseURL?: string;
  /** Agent search categories */
  searchCategories?: KnowledgeBaseCategoryEnum[];
}

/** Agent create/update request body - DTO schema */
export const AgentFormDto = SerdeDtoClass.create<AgentFormInterface>(CatalogDtosKeysEnum.AGENT_FORM_DTO, {
  name: {
    label: 'Название',
    serializer: yup
      .string()
      .min(1, DtoMessageEnum.EMPTY_STRING),
  },
  model: {
    optional: true,
    label: 'Модель',
    serializer: yup.string(),
  },
  type: {
    label: 'Тип',
    serializer: commonEnumValidator(AgentTypeEnum, AgentTypeEnumI18n),
  },
  operatingMode: {
    label: 'Режим работы',
    serializer: commonEnumValidator(AgentOperatingModeEnum, AgentOperatingModeEnumI18n),
  },
  prompt: {
    label: 'Инструкция',
    hint: 'Инструкция-шаблон для агента ИИ, по которой он будет работать',
    serializer: yup
      .string()
      .min(1, DtoMessageEnum.EMPTY_STRING),
  },
  temperature: {
    optional: true,
    label: 'Степень случайности и креативности',
    serializer: yup
      .number()
      .min(0.1, 'Число не может быть меньше 0.1')
      .max(1.0, 'Число не может быть больше 1.0')
      .test(
        'is-decimal',
        'Число должно быть в диапазоне от 0.1 до 1.0',
        value => value === null || value === undefined || (value >= 0.1 && value <= 1.0),
      ),
  },
  minConfidence: {
    optional: true,
    label: 'Минимальная уверенность для ответа',
    serializer: yup
      .number()
      .min(0.1, 'Число не может быть меньше 0.1')
      .max(1.0, 'Число не может быть больше 1.0')
      .test(
        'is-decimal',
        'Число должно быть в диапазоне от 0.1 до 1.0',
        value => value === null || value === undefined || (value >= 0.1 && value <= 1.0),
      ),
  },
  isActive: {
    optional: true,
    label: 'Параметр активности',
    serializer: booleanSchema,
  },
  isEmbedding: {
    optional: (context: SerdeContextInterface<SerdeContextDataInterface, AgentFormInterface>) => context?.value?.operatingMode !== AgentOperatingModeEnum.FRM_MODE,
    label: 'Агент имеет функцию эмбеддинга',
    serializer: booleanSchema
      .test({
        test: (value, context) => {
          if (context?.parent?.operatingMode === AgentOperatingModeEnum.FRM_MODE && value !== true) {
            return context.createError({ message: 'Агент должен иметь функцию эмбеддинга для режима работы ФРМ' });
          }
          return true;
        },
      }),
  },
  isEmbeddingDefault: {
    optional: true,
    label: 'Дефолтный агент для эмбеддинга и поиска в базе знаний',
    serializer: booleanSchema
      .test({
        test: (value, context) => {
          if (value === true && !context?.parent?.isEmbedding) {
            return context.createError({ message: 'Дефолт для эмбеддинга доступен только у агента с включённой функцией эмбеддинга' });
          }
          return true;
        },
      }),
  },
  autoReply: {
    optional: true,
    label: 'Разрешение на автоматический ответ без участия человека',
    serializer: booleanSchema,
  },
  apiKey: {
    label: 'Ключ доступа',
    serializer: yup
      .string()
      .min(1, DtoMessageEnum.EMPTY_STRING),
  },
  isVoiceAssistantDefault: {
    optional: true,
    label: 'Агент по умолчанию для голосового помощника',
    serializer: booleanSchema,
  },
  folderID: {
    optional: true,
    label: 'FolderID',
    serializer: yup
      .string()
      .min(1, DtoMessageEnum.EMPTY_STRING),
  },
  baseURL: {
    optional: (context: SerdeContextInterface<SerdeContextDataInterface, AgentFormInterface>) => context?.value?.type !== AgentTypeEnum.OPENAI,
    label: 'Базовый URL',
    serializer: yup.string(),
  },
  searchCategories: {
    optional: true,
    label: 'Категории поиска',
    serializer: commonEnumValidator.forArray(KnowledgeBaseCategoryEnum, KnowledgeBaseCategoryEnumI18n),
  },
});
